import { useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import Notification from './Notification';

import {
  checkIsDismissNotification,
  getMessageData,
  getNotification,
} from './helpers';

import { AUTO_HIDE_DURATION_TIME, NOTIFICATION_TYPES } from './constants';

import {
  GetDolbyNotificationType,
  GetSpecificNotificationType,
  ShowNotificationType,
  UseNotificationType,
} from './interfaces';
import { StorageTypes } from '../../interfaces/storage';
import { getClientSpecificNotificationTheme } from '@helpers/clientSpecificUtils';

export const useNotification: UseNotificationType = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { t: translate } = useTranslation();

  const showNotification: ShowNotificationType = useCallback(
    ({
      title = '',
      message,
      logMessage = '',
      type,
      closeManually = false,
      withReloadButton = false,
      withDismissButton = true,
      storageType = StorageTypes.Local,
      dismissButtonText,
      reloadPageText,
      dismissButtonAction,
      reloadPageAction,
      ...options
    }) => {
      const isDismissNotification = checkIsDismissNotification(
        message,
        storageType,
      );

      if (isDismissNotification) {
        console.log(logMessage || message);
        return '';
      }

      const notificationId = enqueueSnackbar(message, {
        autoHideDuration: closeManually ? null : AUTO_HIDE_DURATION_TIME,
        preventDuplicate: true,
        content(key) {
          return Notification({
            id: key,
            title,
            message,
            type,
            withReloadButton,
            withDismissButton,
            storageType,
            dismissButtonText,
            reloadPageText,
            dismissButtonAction,
            reloadPageAction,
          });
        },
        ...options,
      });

      return notificationId;
    },
    [enqueueSnackbar],
  );

  const getSuccessNotification: GetSpecificNotificationType = ({
    message,
    ...props
  }) => {
    return getNotification({
      title: translate('notifications.success'),
      message,
      type: NOTIFICATION_TYPES.SUCCESS,
      ...props,
    });
  };

  const getBadRequestNotification: GetSpecificNotificationType = ({
    message,
    ...props
  }) => {
    return getNotification({
      title: translate('notifications.badRequest'),
      message,
      type: NOTIFICATION_TYPES.ERROR,
      ...props,
    });
  };

  const getStreamingNotification: GetSpecificNotificationType = ({
    message = translate('notifications.pleaseReloadPage'),
    ...props
  }) => {
    return getNotification({
      title: translate('notifications.streamingError'),
      message,
      type: NOTIFICATION_TYPES.WARNING,
      ...props,
    });
  };

  const getDolbyNotification: GetDolbyNotificationType = ({
    message = '',
    ...props
  } = {}) => {
    return getNotification({
      title: translate('notifications.dolbyError'),
      message: message || translate('notifications.dolbyErrorText'),
      type: NOTIFICATION_TYPES.ERROR,
      ...props,
    });
  };

  const getMillicastNotification: GetDolbyNotificationType = ({
    message = '',
    ...props
  } = {}) => {
    return getNotification({
      title: translate('notifications.millicast.millicastError'),
      message:
        message || translate('notifications.millicast.millicastErrorText'),
      type: NOTIFICATION_TYPES.ERROR,
      ...props,
    });
  };

  const getWebsocketNotification: GetSpecificNotificationType = ({
    message,
    ...props
  }) => {
    return getNotification({
      title: translate('notifications.websocketError'),
      message,
      type: NOTIFICATION_TYPES.ERROR,
      ...props,
    });
  };

  return {
    showNotification,
    hideNotification: closeSnackbar,
    getSuccessNotification,
    getBadRequestNotification,
    getStreamingNotification,
    getDolbyNotification,
    getMillicastNotification,
    getWebsocketNotification,
  };
};

export const useMessageData = (type?: NOTIFICATION_TYPES) => {
  const { icon, colorIcon } = getMessageData(type ? type : null);
  const themeData = getClientSpecificNotificationTheme();

  return { icon, colorIcon, ...themeData };
};
